/**
 * Video Analytics Service
 * Tracks and posts audience retention data to Supabase
 */

import supabase from "../helper/supabaseClient";

export type VideoCheckpointRecord = {
  video_id: string;
  session_id: string;
  checkpoint_percent: number;
};

export const VIDEO_RETENTION_TABLE = "video_retention_checkpoints";

export const trackVideoCheckpoint = async (
  record: VideoCheckpointRecord
): Promise<boolean> => {
  try {
    const { error } = await supabase.from(VIDEO_RETENTION_TABLE).upsert(
      {
        ...record,
        created_at: new Date().toISOString(),
      },
      {
        onConflict: "video_id,session_id,checkpoint_percent",
      }
    );

    if (error) {
      console.error("Failed to track video checkpoint:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Unexpected checkpoint tracking error:", error);
    return false;
  }
};

export const getRetentionGraphData = async (videoId: string) => {
  const { data, error } = await supabase
    .from(VIDEO_RETENTION_TABLE)
    .select("checkpoint_percent, session_id")
    .eq("video_id", videoId)
    .order("checkpoint_percent", { ascending: true });

  if (error) {
    throw error;
  }

  const sessionIds = new Set<string>();
  const checkpointMap = new Map<number, Set<string>>();

  for (const row of data ?? []) {
    sessionIds.add(row.session_id);

    if (!checkpointMap.has(row.checkpoint_percent)) {
      checkpointMap.set(row.checkpoint_percent, new Set<string>());
    }

    checkpointMap.get(row.checkpoint_percent)?.add(row.session_id);
  }

  const totalViewers = sessionIds.size;
  const checkpoints = Array.from(checkpointMap.keys()).sort((a, b) => a - b);

  return checkpoints.map((checkpoint, index) => {
    const reachedCount = checkpointMap.get(checkpoint)?.size ?? 0;
    const previousCount =
      index === 0
        ? totalViewers
        : checkpointMap.get(checkpoints[index - 1])?.size ?? totalViewers;

    return {
      checkpoint,
      viewers: reachedCount,
      retentionRate:
        totalViewers > 0 ? Math.round((reachedCount / totalViewers) * 100) : 0,
      dropoffCount: Math.max(0, previousCount - reachedCount),
    };
  });
};
